import { useState, useCallback, useEffect } from "react";
import * as PublicService from "$services/Public.service";
import { ShippingRecord } from "$types";
import { OpService } from "$services";

const usePublicShippingRecord = (shippingRecordId?: string) => {
  const [shippingRecord, setShippingRecord] = useState<ShippingRecord>(null);
  const [isFetchingShippingRecord, setIsFetchingShippingRecord] =
    useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchItem = useCallback(
    async (id: string) => {
      try {
        setIsFetchingShippingRecord(true);
        setIsNotFound(false);
        setFetchError(null);

        const result = await PublicService.getPublicShippingRecord(
          "",
          shippingRecordId ?? id
        );

        if (!result?.id) {
          setShippingRecord(null);
          setIsNotFound(true);
          setFetchError("Dossier de transport introuvable.");
          return;
        }

        const shipments = Array.isArray(result.shipments)
          ? result.shipments
          : [];

        const shipment = {
          ...result,
          shipments: await Promise.all(
            shipments.map(async (shipment) => ({
              ...shipment,
              op:
                shipment.op ??
                (await OpService.getPublicOpById(shipment.opId))?.data,
            }))
          ),
        };

        setShippingRecord(shipment);
      } catch (error: any) {
        console.error("Error fetching shipping record:", error);
        setShippingRecord(null);
        if (error?.response?.status === 404) {
          setIsNotFound(true);
          setFetchError(
            error.response?.data?.message ?? "Dossier de transport introuvable."
          );
        } else {
          setFetchError(
            error.response?.data?.message ??
              error.message ??
              "Impossible de charger le dossier de transport."
          );
        }
      } finally {
        setIsFetchingShippingRecord(false);
      }
    },
    [shippingRecordId]
  );

  useEffect(() => {
    setShippingRecord(null);
    setIsNotFound(false);
    setFetchError(null);
    if (shippingRecordId) {
      fetchItem(shippingRecordId);
    }
  }, [fetchItem, shippingRecordId]);

  return {
    shippingRecord,
    isFetchingShippingRecord,
    isNotFound,
    fetchError,
  };
};

export default usePublicShippingRecord;
